/**
 * PHYSICAL-RISK-METER
 * KPI display with a gauge the final risk level
 */

import { useId } from "react"
import Chip from "@/components/chip"
import RiskDot from "@/components/RiskDot"
import { useAppSelector } from "@/core/root-reducer"
import { RISK_RESIDUAL_SCALES } from "@/redux/account.types"
import getColor from "@/utils/get-color.utils"
import { PhysicalRisk } from "../model/physical-risk.types"

const INACTIVE_COLOR = "#E2E5E9"
const SCORE_COLOR = "#111827"
const MUTED_COLOR = "#9CA3AF"
const GRADIENT_END_OPACITY = 0.45

interface Props {
  risk: PhysicalRisk | null
  size: "sm" | "lg"
}

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, value))
}

export function PhysicalRiskMeter(props: Props) {
  const width = props.size === "lg" ? 96 : 38
  const height = Math.round(width * 0.72)
  const shouldDisplayMaxScore = props.size !== "sm"

  const score = props.risk?.cValueNormalized
  const riskIndex = Math.max(0, Math.min(3, props.risk?.cValueEffectiveIndex ?? 0))
  const activeColor = props.risk?.cValueEffectiveColor || getColor(RISK_RESIDUAL_SCALES[riskIndex]?.color ?? "green")
  const activeGradientId = `physical-risk-meter-gradient-${useId().replace(/:/g, "")}`
  const isIdtreeScoreHidden = useAppSelector((state) => state._session.accountRisksSettings.isIdtreeScoreHidden)
  const showCalculations = useAppSelector((state) => state._session.userOptions.risksShowCalculations)

  //Sans note IDTree : le niveau de risque residuel (chip en grand, pastille en petit), suivi du score si l'utilisateur l'affiche
  if (isIdtreeScoreHidden) {
    const residualScore = props.risk && showCalculations ? Math.round(props.risk.cValueEffective ?? 0) : null
    const dotColor = props.risk ? activeColor : INACTIVE_COLOR

    if (props.size === "lg") {
      return (
        <Chip color={activeColor}>
          {`${props.risk?.cValueEffectiveLabel ?? ""}${residualScore !== null ? ` (${residualScore})` : ""}`}
        </Chip>
      )
    }

    return (
      <div className="flex items-center gap-[5px]" style={{ height }}>
        <RiskDot color={dotColor} isSmall={residualScore !== null} />
        {residualScore !== null && (
          <span className="whitespace-nowrap font-bold" style={{ color: activeColor }}>
            {residualScore}
          </span>
        )}
      </div>
    )
  }

  const cx = 60
  const cy = 70
  const radius = 48
  const strokeWidth = 12
  const gap = 20
  const totalAngle = 240
  const startAngle = 150
  const segmentAngle = (totalAngle - gap * 3) / 4
  const segmentMinScore = 10 - riskIndex * 3
  const normalizedScore = typeof score === "number" ? score : segmentMinScore
  const scoreText = props.risk ? normalizedScore.toString() : "-"
  const filledSegments = props.risk ? clamp(normalizedScore / 10, 0, 1) * 4 : 0

  const polar = (angle: number) => {
    const rad = (angle * Math.PI) / 180
    return {
      x: cx + radius * Math.cos(rad),
      y: cy + radius * Math.sin(rad)
    }
  }

  const arc = (start: number, end: number) => {
    const p1 = polar(start)
    const p2 = polar(end)

    return `M ${p1.x} ${p1.y} A ${radius} ${radius} 0 0 1 ${p2.x} ${p2.y}`
  }

  const segments = Array.from({ length: 4 }, (_, i) => {
    const start = startAngle + i * (segmentAngle + gap)
    const end = start + segmentAngle
    const fillRatio = clamp(filledSegments - i, 0, 1)
    const activeEnd = start + segmentAngle * fillRatio
    const activeStartPoint = polar(start)
    const activeEndPoint = polar(activeEnd)
    const opacityRange = 1 - GRADIENT_END_OPACITY
    const startProgress = filledSegments > 0 ? i / filledSegments : 0
    const endProgress = filledSegments > 0 ? (i + fillRatio) / filledSegments : 0

    return {
      d: arc(start, end),
      activeD: fillRatio > 0 ? arc(start, activeEnd) : null,
      gradientId: `${activeGradientId}-${i}`,
      gradient: {
        x1: activeStartPoint.x,
        y1: activeStartPoint.y,
        x2: activeEndPoint.x,
        y2: activeEndPoint.y,
        startOpacity: 1 - opacityRange * startProgress,
        endOpacity: 1 - opacityRange * endProgress
      }
    }
  })

  return (
    <div className="flex items-center justify-center -m-[2px]" style={{ width, height }}>
      <svg viewBox="0 18 120 84" width="100%" height="100%">
        <defs>
          {segments.map(
            (segment) =>
              segment.activeD && (
                <linearGradient
                  key={segment.gradientId}
                  id={segment.gradientId}
                  x1={segment.gradient.x1}
                  y1={segment.gradient.y1}
                  x2={segment.gradient.x2}
                  y2={segment.gradient.y2}
                  gradientUnits="userSpaceOnUse">
                  <stop offset="0%" stopColor={activeColor} stopOpacity={segment.gradient.startOpacity} />
                  <stop offset="100%" stopColor={activeColor} stopOpacity={segment.gradient.endOpacity} />
                </linearGradient>
              )
          )}
        </defs>

        {segments.map((s, i) => (
          <path
            key={`inactive-${i}`}
            d={s.d}
            fill="none"
            stroke={INACTIVE_COLOR}
            strokeLinecap="round"
            strokeWidth={strokeWidth}
          />
        ))}
        {segments.map(
          (s, i) =>
            s.activeD && (
              <path
                key={`active-${i}`}
                d={s.activeD}
                fill="none"
                stroke={`url(#${s.gradientId})`}
                strokeLinecap="round"
                strokeWidth={strokeWidth}
              />
            )
        )}

        <text x={cx} y="74" dominantBaseline="middle" textAnchor="middle">
          <tspan
            fill={props.risk ? SCORE_COLOR : MUTED_COLOR}
            fontSize={shouldDisplayMaxScore ? 34 : 40}
            fontWeight="bold">
            {scoreText}
          </tspan>
          {shouldDisplayMaxScore && props.risk && (
            <tspan fill={MUTED_COLOR} fontSize={16} fontWeight="bold" dx="3">
              /10
            </tspan>
          )}
        </text>
      </svg>
    </div>
  )
}
